import { useMemo } from 'react';

// third-party
import useSWR, { mutate } from 'swr';

// types
import { SnackbarProps } from 'types/snackbar';

// ==============================|| API - SNACKBAR ||============================== //

const endpoints = {
  key: 'snackbar'
};

const initialState: SnackbarProps = {
  action: false,
  open: false,
  message: 'Note archived',
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right'
  },
  variant: 'default',
  alert: {
    color: 'primary',
    variant: 'filled'
  },
  transition: 'Fade',
  close: false,
  actionButton: false,
  maxStack: 3,
  dense: false,
  iconVariant: 'usedefault'
};

export function useGetSnackbar() {
  const { data } = useSWR(endpoints.key, () => initialState, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(() => ({ snackbar: data! }), [data]);

  return memoizedValue;
}

export function openSnackbar(snackbar: SnackbarProps) {
  // to update local state based on key

  const { action, open, message, anchorOrigin, variant, alert, transition, close, actionButton } = snackbar;

  mutate<SnackbarProps>(
    endpoints.key,
    (currentSnackbar: SnackbarProps | undefined) => {
      const base = currentSnackbar ?? initialState;
      return {
        ...base,
        action: action ?? base.action,
        open: open ?? base.open,
        message: message ?? base.message,
        anchorOrigin: anchorOrigin ?? base.anchorOrigin,
        variant: variant ?? base.variant,
        alert: {
          color: alert?.color ?? base.alert.color,
          variant: alert?.variant ?? base.alert.variant
        },
        transition: transition ?? base.transition,
        close: close ?? base.close,
        actionButton: actionButton ?? base.actionButton
      };
    },
    false
  );
}

export function closeSnackbar() {
  // to update local state based on key
  mutate<SnackbarProps>(endpoints.key, (currentSnackbar: SnackbarProps | undefined) => {
    const base = currentSnackbar ?? initialState;
    return { ...base, open: false };
  }, false);
}

export function handlerIncrease(maxStack: number) {
  // to update local state based on key
  mutate<SnackbarProps>(endpoints.key, (currentSnackbar: SnackbarProps | undefined) => {
    const base = currentSnackbar ?? initialState;
    return { ...base, maxStack };
  }, false);
}

export function handlerDense(dense: boolean) {
  // to update local state based on key
  mutate<SnackbarProps>(endpoints.key, (currentSnackbar: SnackbarProps | undefined) => {
    const base = currentSnackbar ?? initialState;
    return { ...base, dense };
  }, false);
}

export function handlerIconVariants(iconVariant: string) {
  // to update local state based on key
  mutate<SnackbarProps>(endpoints.key, (currentSnackbar: SnackbarProps | undefined) => {
    const base = currentSnackbar ?? initialState;
    return { ...base, iconVariant, hideIconVariant: iconVariant === 'hide' };
  }, false);
}
